import { afterMidnightAllowed, findOverlapWithin, sessionMinutes } from '@his/shared';

export const DEFAULT_FROM = '09:00';
export const DEFAULT_TO = '13:00';
export const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0] as const;
const LAST_START_MINUTE = 23 * 60 + 50;
const NEXT_PERIOD_MINUTES = 180;

export interface StoredPeriod {
  weekday: number;
  startTime: string;
  endTime: string;
  slotMinutes?: number;
  maxOverbook?: number;
  portalBookable?: boolean;
  capacity?: number;
  maxWalkIns?: number;
  roomLabel?: string;
  portalMaxPerSession?: number | null;
}

export interface HiddenValues {
  capacity?: number;
  maxWalkIns?: number;
  roomLabel?: string;
  portalMaxPerSession?: number | null;
}

export interface Period {
  key: string;
  from: string;
  to: string;
  overbook: number;
  bookableOnline: boolean;
  hidden: HiddenValues | null;
}

export interface DayHours {
  working: boolean;
  periods: Period[];
}

export type WeekHours = DayHours[];

export interface SavedPeriod {
  weekday: number;
  startTime: string;
  endTime: string;
  maxOverbook: number;
  portalBookable: boolean;
  capacity?: number;
  maxWalkIns?: number;
  roomLabel?: string;
  portalMaxPerSession?: number | null;
}

export type WeekIssue =
  | { kind: 'nothing_working' }
  | { kind: 'ends_before_start'; weekday: number; from: string; to: string }
  | { kind: 'overlap'; weekday: number; first: string; second: string };

let counter = 0;
const nextKey = (): string => {
  counter += 1;
  return `p${counter}`;
};

const toMinutes = (hhmm: string): number => {
  const [hour, minute] = hhmm.split(':').map(Number);
  return hour * 60 + minute;
};

const toHhmm = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

export function newPeriod(from = DEFAULT_FROM, to = DEFAULT_TO): Period {
  return { key: nextKey(), from, to, overbook: 0, bookableOnline: true, hidden: null };
}

export function emptyWeek(): WeekHours {
  return Array.from({ length: 7 }, () => ({ working: false, periods: [] }));
}

function hiddenOf(slot: StoredPeriod): HiddenValues | null {
  const hidden: HiddenValues = {};
  if (slot.capacity !== undefined) hidden.capacity = slot.capacity;
  if (slot.maxWalkIns !== undefined) hidden.maxWalkIns = slot.maxWalkIns;
  if (slot.roomLabel) hidden.roomLabel = slot.roomLabel;
  if (slot.portalMaxPerSession != null) hidden.portalMaxPerSession = slot.portalMaxPerSession;
  return Object.keys(hidden).length > 0 ? hidden : null;
}

export function weekFromSlots(slots: readonly StoredPeriod[]): WeekHours {
  const week = emptyWeek();
  const ordered = [...slots].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
  for (const slot of ordered) {
    const day = week[slot.weekday];
    if (!day) continue;
    day.working = true;
    day.periods.push({
      key: nextKey(),
      from: slot.startTime,
      to: slot.endTime,
      overbook: slot.maxOverbook ?? 0,
      bookableOnline: slot.portalBookable !== false,
      hidden: hiddenOf(slot),
    });
  }
  return week;
}

export function slotsFromWeek(week: WeekHours): SavedPeriod[] {
  return week.flatMap((day, weekday) =>
    day.working
      ? day.periods.map((period) => ({
          weekday,
          startTime: period.from,
          endTime: period.to,
          maxOverbook: period.overbook,
          portalBookable: period.bookableOnline,
          ...(period.hidden ?? {}),
        }))
      : [],
  );
}

function withDay(week: WeekHours, weekday: number, change: (day: DayHours) => DayHours): WeekHours {
  return week.map((day, index) => (index === weekday ? change(day) : day));
}

export function setWorking(week: WeekHours, weekday: number, working: boolean): WeekHours {
  return withDay(week, weekday, (day) => ({
    working,
    periods: working && day.periods.length === 0 ? [newPeriod()] : day.periods,
  }));
}

export function nextPeriodAfter(periods: readonly Period[]): Period {
  const last = periods[periods.length - 1];
  if (!last) return newPeriod();
  const start = sessionMinutes(last.from, last.to).end % 1440;
  if (start === 0 || start >= LAST_START_MINUTE) return newPeriod();
  const end = Math.min(start + NEXT_PERIOD_MINUTES, LAST_START_MINUTE);
  return newPeriod(toHhmm(start), toHhmm(end));
}

export function addPeriod(week: WeekHours, weekday: number): WeekHours {
  return withDay(week, weekday, (day) => ({ working: true, periods: [...day.periods, nextPeriodAfter(day.periods)] }));
}

export function removePeriod(week: WeekHours, weekday: number, key: string): WeekHours {
  return withDay(week, weekday, (day) => {
    const periods = day.periods.filter((period) => period.key !== key);
    return { working: periods.length > 0 && day.working, periods };
  });
}

export function patchPeriod(week: WeekHours, weekday: number, key: string, changes: Partial<Omit<Period, 'key'>>): WeekHours {
  return withDay(week, weekday, (day) => ({
    ...day,
    periods: day.periods.map((period) => {
      if (period.key !== key) return period;
      const merged = { ...period, ...changes };
      if (changes.from !== undefined && changes.to === undefined && merged.from >= merged.to) {
        const length = Math.max(10, toMinutes(period.to) - toMinutes(period.from));
        merged.to = toHhmm(Math.min(toMinutes(merged.from) + length, 1430));
      }
      return merged;
    }),
  }));
}

function shape(day: DayHours): string {
  if (!day.working) return '';
  return JSON.stringify(day.periods.map((period) => [period.from, period.to, period.overbook, period.bookableOnline]));
}

function isFilled(day: DayHours): boolean {
  return day.working && day.periods.length > 0;
}

export function daysOverwrittenBy(week: WeekHours, source: number): number[] {
  const sourceShape = shape(week[source]);
  return week.flatMap((day, weekday) => (weekday !== source && isFilled(day) && shape(day) !== sourceShape ? [weekday] : []));
}

export function applyToAllDays(week: WeekHours, source: number): WeekHours {
  const from = week[source];
  return week.map((day, weekday) =>
    weekday === source
      ? day
      : {
          working: from.working,
          periods: from.periods.map((period) => ({ ...period, key: nextKey(), hidden: null })),
        },
  );
}

export type PeriodIssue = { kind: 'ends_before_start' } | { kind: 'overlap'; range: string };

export function periodIssues(day: DayHours, dayClosesAt: number): Map<string, PeriodIssue> {
  const issues = new Map<string, PeriodIssue>();
  if (!day.working) return issues;
  const spans = day.periods.map((period) => ({ period, span: sessionMinutes(period.from, period.to) }));
  for (const { period } of spans) {
    if (!afterMidnightAllowed(period.from, period.to, dayClosesAt)) issues.set(period.key, { kind: 'ends_before_start' });
  }
  for (const current of spans) {
    if (issues.has(current.period.key)) continue;
    const clash = spans.find(
      (other) =>
        other.period.key !== current.period.key &&
        !issues.has(other.period.key) &&
        current.span.start < other.span.end &&
        other.span.start < current.span.end,
    );
    if (clash) issues.set(current.period.key, { kind: 'overlap', range: `${clash.period.from}–${clash.period.to}` });
  }
  return issues;
}

export function periodTimeBlocked(day: DayHours, key: string, side: 'from' | 'to', time: string, dayClosesAt: number): boolean {
  const current = day.periods.find((period) => period.key === key);
  if (!current || !day.working) return false;
  if (side === 'from' && time >= current.to) return false;
  if (side === 'to' && time <= current.from) return !afterMidnightAllowed(current.from, time, dayClosesAt);
  const candidate = side === 'from' ? sessionMinutes(time, current.to) : sessionMinutes(current.from, time);
  return day.periods.some((other) => {
    if (other.key === key) return false;
    const span = sessionMinutes(other.from, other.to);
    return candidate.start < span.end && span.start < candidate.end;
  });
}

export function weekIssue(week: WeekHours, dayClosesAt: number): WeekIssue | null {
  const slots = slotsFromWeek(week);
  if (slots.length === 0) return { kind: 'nothing_working' };
  const wrong = slots.find((slot) => !afterMidnightAllowed(slot.startTime, slot.endTime, dayClosesAt));
  if (wrong) return { kind: 'ends_before_start', weekday: wrong.weekday, from: wrong.startTime, to: wrong.endTime };
  const overlap = findOverlapWithin(slots);
  if (overlap) {
    return {
      kind: 'overlap',
      weekday: overlap.first.weekday,
      first: `${overlap.first.startTime}–${overlap.first.endTime}`,
      second: `${overlap.second.startTime}–${overlap.second.endTime}`,
    };
  }
  return null;
}

export function orderedWeekdays(weekStartsOn: number = 1): number[] {
  return Array.from({ length: 7 }, (_, offset) => (weekStartsOn + offset) % 7);
}

export function weekSummary(slots: readonly StoredPeriod[], dayLabel: (weekday: number) => string, weekStartsOn: number = 1): string[] {
  const week = weekFromSlots(slots);
  return orderedWeekdays(weekStartsOn).flatMap((weekday) => {
    const day = week[weekday];
    if (!day.working || day.periods.length === 0) return [];
    return [`${dayLabel(weekday)} ${day.periods.map((period) => `${period.from}–${period.to}`).join(', ')}`];
  });
}

export const CLINIC_HOURS_PAGE = 100;

export interface ClinicHoursRow {
  id: string;
  doctorId: string;
  branchId: string;
  departmentId: string | null;
  department: { id: string; name: string } | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  slots: StoredPeriod[];
  doctor: { id: string; name: string; designation: string | null };
}

export interface ClinicHoursDoctor {
  id: string;
  name: string;
  departmentId?: string | null;
  departments?: { id: string; name: string }[];
}

export function weekdayLabel(weekday: number, locale: string, style: 'short' | 'long'): string {
  return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2023, 0, 1 + weekday)));
}

export function doctorsOfBranch<T extends ClinicHoursDoctor>(doctors: readonly T[]): T[] {
  return doctors.filter((doctor) => (doctor.departments?.length ?? 0) > 0);
}

export function departmentsOf<T extends ClinicHoursDoctor>(doctors: readonly T[]): { id: string; name: string }[] {
  const byId = new Map<string, { id: string; name: string }>();
  doctors.forEach((doctor) => doctor.departments?.forEach((department) => byId.set(department.id, department)));
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function mergeDepartments(
  branchDepartments: readonly { id: string; name: string }[],
  fromDoctors: readonly { id: string; name: string }[],
): { id: string; name: string }[] {
  const byId = new Map<string, { id: string; name: string }>();
  [...branchDepartments, ...fromDoctors].forEach((department) => byId.set(department.id, { id: department.id, name: department.name }));
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function doctorsInDepartment<T extends ClinicHoursDoctor>(doctors: readonly T[], departmentId: string): T[] {
  if (!departmentId) return [];
  return doctors.filter((doctor) => doctor.departments?.some((department) => department.id === departmentId));
}

export function doctorsWithoutDepartment<T extends ClinicHoursDoctor>(doctors: readonly T[]): T[] {
  return doctors.filter((doctor) => (doctor.departments?.length ?? 0) === 0);
}

export function pickOne(options: readonly { id: string }[], chosen: string): string {
  if (chosen && options.some((option) => option.id === chosen)) return chosen;
  return options.length === 1 ? options[0].id : '';
}

export function departmentFor(doctor: ClinicHoursDoctor | undefined, chosen: string): string {
  const options = doctor?.departments ?? [];
  if (chosen && options.some((department) => department.id === chosen)) return chosen;
  return options.length === 1 ? options[0].id : '';
}

export function rowForDepartment(rows: readonly ClinicHoursRow[], departmentId: string): ClinicHoursRow | null {
  return rows.find((row) => row.departmentId === departmentId) ?? null;
}
